import jwt from "jsonwebtoken";
import { DateTime } from "luxon";
import { createContext, FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useGraphQL } from "../../api/context/graphql-context";
import {
  User,
  UserInformation,
  useUserInformationLazyQuery,
  useUserLazyQuery,
} from "../../api/smartmelts-api/graphql/generated";
import { LOCAL_STORAGE_KEY } from "../../models/local-storage-keys";

interface IUserContext {
  logout: () => void;
  isLoggedIn: () => boolean;
  fetchUser: () => void;
  fetchUserInformation: () => void;
  user?: User;
  userInformation?: UserInformation;
  isDemo: boolean;
}

export const UserContext = createContext<IUserContext>({} as IUserContext);

interface IUserContextProviderProps {}

export const UserContextProvider: FunctionComponent<IUserContextProviderProps> = (props) => {
  const { setAuthorizationHeader } = useGraphQL();

  const logout = useCallback(() => {
    setAuthorizationHeader(null);
  }, [setAuthorizationHeader]);

  const [user, setUser] = useState<User>();

  const [userDataQuery] = useUserLazyQuery({
    variables: { useDemoIfNoReportAvailable: true },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data && data.user) {
        setUser(data.user);
      }
    },
    onError: () => logout(),
  });

  const [userInformation, setUserInformation] = useState<UserInformation>();
  const [userInformationQuery] = useUserInformationLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data && data?.userinformation) {
        setUserInformation(data.userinformation);
      }
    },
    onError: () => logout(),
  });

  useEffect(() => {
    if (!!user?.onboardedAt) {
      userInformationQuery();
    }
  }, [user, userInformationQuery]);

  const fetchUser = useCallback(() => {
    userDataQuery();
  }, [userDataQuery]);

  const fetchUserInformation = useCallback(() => {
    if (!!user?.onboardedAt) {
      userInformationQuery();
    }
  }, [user, userInformationQuery]);

  const isLoggedIn = useCallback(() => {
    const accessToken = localStorage.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN) || "";
    const lastAction = DateTime.fromISO(localStorage.getItem(LOCAL_STORAGE_KEY.LAST_ACTION) || "");
    const payload = jwt.decode(accessToken) as any;

    if (!payload || payload.sub !== "AUTHORIZED") {
      return false;
    }
    const expires = new Date(payload.exp * 1000);
    const now = new Date();

    if (now > expires || (lastAction && lastAction.diffNow("minutes").minutes < -30)) {
      logout();
      return false;
    }

    return true;
  }, [logout]);

  const isDemo = useMemo(() => {
    if (!user?.dnaReport) {
      return true;
    }
    return user.dnaReport.isDemo;
  }, [user]);

  return (
    <UserContext.Provider
      value={{ logout, isLoggedIn, fetchUser, fetchUserInformation, user, userInformation, isDemo }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  return useContext(UserContext);
};
